package com.sddamico.swigmore.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.swigmore.components.layouts.Column
import com.sddamico.swigmore.components.layouts.Columns
import com.sddamico.swigmore.components.layouts.ContentColumn
import com.sddamico.swigmore.components.layouts.PageLayout
import com.sddamico.swigmore.components.sections.HeaderAccount
import com.sddamico.swigmore.ds.Reveal
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.brand.Crest
import com.sddamico.swigmore.ds.brand.CrestVariant
import com.sddamico.swigmore.ds.brand.Wordmark
import com.sddamico.swigmore.ds.core.Avatar
import com.sddamico.swigmore.ds.core.Card
import com.sddamico.swigmore.ds.core.CardVariant
import com.sddamico.swigmore.ds.core.Eyebrow
import com.sddamico.swigmore.ds.core.PageTitle
import com.sddamico.swigmore.ds.core.PanelLabel
import com.sddamico.swigmore.ds.core.SectionHeader
import com.sddamico.swigmore.ds.core.SortableTable
import com.sddamico.swigmore.ds.core.StatusTag
import com.sddamico.swigmore.ds.core.SubmissionStatus
import com.sddamico.swigmore.ds.core.SyncBadge
import com.sddamico.swigmore.ds.core.TableColumn
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.feedback.Banner
import com.sddamico.swigmore.ds.feedback.Countdown
import com.sddamico.swigmore.ds.feedback.CountdownTone
import com.sddamico.swigmore.ds.feedback.FeedRow
import com.sddamico.swigmore.ds.feedback.PageTitleSkeleton
import com.sddamico.swigmore.ds.feedback.PushPreview
import com.sddamico.swigmore.ds.feedback.Skeleton
import com.sddamico.swigmore.ds.feedback.SkeletonBlock
import com.sddamico.swigmore.ds.feedback.StatTile
import com.sddamico.swigmore.ds.forms.Button
import com.sddamico.swigmore.ds.forms.ButtonStyle
import com.sddamico.swigmore.ds.forms.ButtonVariant
import com.sddamico.swigmore.ds.forms.IconButton
import com.sddamico.swigmore.ds.forms.IconButtonVariant
import com.sddamico.swigmore.ds.forms.SelectField
import com.sddamico.swigmore.ds.forms.TextField
import com.sddamico.swigmore.ds.forms.TextFieldVariant
import com.sddamico.swigmore.ds.forms.Toggle
import com.sddamico.swigmore.ds.poll.BallotSlot
import com.sddamico.swigmore.ds.poll.BallotReviewCard
import com.sddamico.swigmore.ds.poll.FieldTeamRow
import com.sddamico.swigmore.ds.poll.ReviewNoteView
import com.sddamico.swigmore.ds.poll.PreviousPickRow
import com.sddamico.swigmore.ds.poll.MatchupCard
import com.sddamico.swigmore.ds.poll.MatchupSide
import com.sddamico.swigmore.ds.poll.Movement
import com.sddamico.swigmore.ds.poll.RankCard
import com.sddamico.swigmore.ds.poll.RankHistoryRow
import com.sddamico.swigmore.ds.poll.RankHistoryTable
import com.sddamico.swigmore.ds.poll.SlotDragHandlers
import com.sddamico.swigmore.ds.poll.SubmissionRow
import com.sddamico.swigmore.ds.poll.SubmissionTable
import com.sddamico.swigmore.ds.poll.WeekCard
import com.sddamico.swigmore.ds.typeBody
import com.sddamico.swigmore.ds.typeDisplay
import com.sddamico.swigmore.ds.typeMono
import com.sddamico.swigmore.models.PollRules
import com.sddamico.swigmore.models.PollStanding
import com.sddamico.swigmore.util.AppTab
import com.sddamico.swigmore.util.NOT_FINAL
import com.sddamico.swigmore.util.deadlineTime
import com.sddamico.swigmore.util.easternHour
import com.sddamico.swigmore.util.easternShortDayTime
import com.sddamico.swigmore.util.rememberNow
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import kotlin.js.Date
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Text

/**
 * Living catalogue of the Swigmore design system, populated with the sample data from the
 * `Swigmore Poll` design. Not linked from the app; visit /design-system directly.
 */
@Page
@Composable
fun DesignSystemPage() {
  PageLayout("Design system", activeTab = AppTab.Poll, syncStatus = "Fantrax synced 4m ago", account = HeaderAccount("sk@example.com", "Stephen Kaye") {}) {
    ContentColumn {
      PageTitle(
        eyebrow = "Swigmore Poll · Design system",
        title = "The Playbook",
        lede = "Every token and component the app is built from, with the sample data from the design.",
        titleSize = "clamp(32px,5vw,54px)",
      )
      Foundations()
      BrandAndCore()
      Controls()
      PollComponents()
      BallotComponents()
      CommishComponents()
      LoadingComponents()
    }
  }
}

@Composable
private fun Block(title: String, caption: String? = null, content: @Composable () -> Unit) {
  Section(attrs = Modifier.css("margin-top" to "40px").toAttrs()) {
    SectionHeader(title, caption = caption)
    content()
  }
}

@Composable
private fun Foundations() {
  Block("Color", "Palette A · Green") {
    Div(attrs = Modifier.css("display" to "grid", "grid-template-columns" to "repeat(auto-fit,minmax(130px,1fr))", "gap" to "10px").toAttrs()) {
      listOf(
        "field900" to SwColor.field900, "field800" to SwColor.field800, "field700 · bg" to SwColor.field700,
        "cream" to SwColor.cream, "creamDeep" to SwColor.creamDeep, "creamLight" to SwColor.creamLight,
        "paper" to SwColor.paper, "gold" to SwColor.gold,
      ).forEach { (name, hex) ->
        Div(attrs = Modifier.css("border-radius" to "9px", "overflow" to "hidden", "border" to "1px solid ${SwColor.hairline}").toAttrs()) {
          Div(attrs = Modifier.css("height" to "64px", "background" to hex).toAttrs())
          Div(attrs = Modifier.typeMono("10px").css("padding" to "8px 10px", "background" to SwColor.field800).toAttrs()) { Text("$name  $hex") }
        }
      }
    }
  }
  Block("Type", "Graduate · Archivo · JetBrains Mono") {
    Card(variant = CardVariant.Field) {
      Div(attrs = Modifier.typeDisplay("clamp(32px,5vw,54px)").toAttrs()) { Text("The Prison Boys Hold the Line") }
      Eyebrow("Week 8 poll · Released Fri, Oct 23 · 30 of 30 ballots", Modifier.css("margin-top" to "14px"))
      Div(attrs = Modifier.typeBody("16px").css("color" to SwColor.textSecondary, "margin-top" to "10px", "max-width" to "620px").toAttrs()) {
        Text("Twenty-one first-place votes, an unblemished 8-0 and absolutely zero remorse. MACtion Daddy climbs to No. 2.")
      }
      Div(attrs = Modifier.typeMono().css("color" to SwColor.textMuted, "margin-top" to "10px").toAttrs()) {
        Text("8–1 SCORING · MAX 240 · TAP A TEAM FOR THE BREAKDOWN")
      }
    }
  }
  Block("Surfaces", "Card variants") {
    Div(attrs = Modifier.css("display" to "grid", "grid-template-columns" to "repeat(auto-fit,minmax(180px,1fr))", "gap" to "12px").toAttrs()) {
      CardVariant.entries.forEach { v ->
        Card(variant = v) {
          Div(attrs = Modifier.typeDisplay("20px", "normal").toAttrs()) { Text(v.name) }
          Div(attrs = Modifier.typeBody("12px", 500, "1.4").css("opacity" to ".75", "margin-top" to "4px").toAttrs()) {
            Text(
              when (v) {
                CardVariant.Ticket -> "Cream stock with the dashed stitch."
                CardVariant.Field -> "Inset panel on the green field."
                CardVariant.Plain -> "Cream without the stitch."
                CardVariant.Gold -> "Confirmation band."
                CardVariant.Paper -> "Email preview sheet."
              }
            )
          }
        }
      }
    }
  }
}

@Composable
private fun BrandAndCore() {
  Block("Brand & badges") {
    Div(attrs = Modifier.css("display" to "flex", "flex-wrap" to "wrap", "gap" to "18px", "align-items" to "center").toAttrs()) {
      Wordmark()
      Crest(variant = CrestVariant.Gold, size = 54)
      Div(attrs = Modifier.css("background" to SwColor.cream, "padding" to "10px", "border-radius" to "10px").toAttrs()) {
        Crest(variant = CrestVariant.Field, size = 54)
      }
      SyncBadge("Fantrax synced 4m ago")
      Avatar("SK")
      SubmissionStatus.entries.forEach { s ->
        Div(attrs = Modifier.css("background" to SwColor.cream, "padding" to "6px 8px", "border-radius" to "6px").toAttrs()) { StatusTag(s) }
      }
    }
  }
}

@Composable
private fun Controls() {
  var email by remember { mutableStateOf("") }
  var code by remember { mutableStateOf("") }
  var query by remember { mutableStateOf("") }
  var team by remember { mutableStateOf<String?>(null) }
  var autoSend by remember { mutableStateOf(true) }
  Block("Controls", "Buttons · inputs") {
    Columns(gap = "16px") {
      Column("320px") {
        Card(variant = CardVariant.Ticket, padding = "18px") {
          Div(attrs = Modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "10px").toAttrs()) {
            Button("Cast Your Ballot", fullWidth = true)
            Button("Disabled", disabled = true, fullWidth = true)
            Div(attrs = Modifier.css("display" to "flex", "gap" to "8px", "flex-wrap" to "wrap").toAttrs()) {
              Button("Amend", style = ButtonStyle.Caps)
              Button("Withdraw", variant = ButtonVariant.Outline, style = ButtonStyle.Caps)
              Button("Edit", variant = ButtonVariant.Outline, style = ButtonStyle.CapsSm)
              Button("Clear", variant = ButtonVariant.Ghost, style = ButtonStyle.Caps)
              Button("Rewrite with Claude", variant = ButtonVariant.Outline, style = ButtonStyle.CapsSm, ai = true)
            }
            Div(attrs = Modifier.css("display" to "flex", "gap" to "6px").toAttrs()) {
              IconButton("▲", "Up"); IconButton("▼", "Down")
              IconButton("×", "Remove", variant = IconButtonVariant.Bare)
              IconButton("+", "Expand", variant = IconButtonVariant.Round)
              IconButton("+", "Add", variant = IconButtonVariant.Add)
            }
          }
        }
      }
      Column("320px") {
        Card(variant = CardVariant.Field, padding = "18px") {
          Div(attrs = Modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "10px").toAttrs()) {
            Button("Submit Ballot", variant = ButtonVariant.Gold, fullWidth = true)
            Button("Fill 2 more slots", variant = ButtonVariant.Gold, disabled = true, fullWidth = true)
            Div(attrs = Modifier.css("display" to "flex", "gap" to "8px", "flex-wrap" to "wrap").toAttrs()) {
              Button("Preview announcement", variant = ButtonVariant.Outline, style = ButtonStyle.Caps, onDark = true)
              Button("Publish week 9 poll", variant = ButtonVariant.Gold, style = ButtonStyle.Caps)
              Button("Clear", variant = ButtonVariant.Ghost, style = ButtonStyle.Caps, onDark = true)
              Button("Review ballot", variant = ButtonVariant.Outline, style = ButtonStyle.Caps, onDark = true, ai = true)
            }
            TextField(query, { query = it }, variant = TextFieldVariant.Search, placeholder = "Search teams…")
            Toggle("Auto-send", autoSend, onChange = { autoSend = it })
          }
        }
      }
      Column("320px") {
        Card(variant = CardVariant.Ticket, padding = "18px") {
          Div(attrs = Modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "10px").toAttrs()) {
            TextField(email, { email = it }, placeholder = "you@example.com", type = "email")
            TextField(code, { code = it }, variant = TextFieldVariant.Code, placeholder = "000000", label = "Six-digit code")
            SelectField(team, { team = it }, listOf("1" to "Swamp Kings (7-1)", "2" to "MACtion Daddy (6-2)", "3" to "Fire Norvell (6-2)"),
              placeholder = "Pick a team", label = "Team", unavailable = setOf("3"))
          }
        }
      }
    }
  }
}

private val SampleDist = listOf(
  listOf(21, 6, 2, 1, 0, 0, 0, 0), listOf(6, 14, 6, 2, 1, 1, 0, 0), listOf(3, 6, 12, 5, 2, 1, 1, 0),
)

@Composable
private fun PollComponents() {
  var open by remember { mutableStateOf(0) }
  val now = rememberNow().toDouble()
  val remaining = nextFridayDeadline(now) - now

  Block("Poll", "Rank cards · countdown · matchups") {
    Columns {
      Column("520px") {
        Div(attrs = Modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "8px").toAttrs()) {
          listOf(
            Triple("Harbaugh/Stalions 4 Prison", "8-0 · 21 first-place votes", 227 to Movement.Same),
            Triple("MACtion Daddy", "7-1 · 6 first-place votes", 199 to Movement.Up(1)),
            Triple("Swamp Kings", "7-1 · 3 first-place votes", 182 to Movement.Down(1)),
          ).forEachIndexed { i, (team, meta, pm) ->
            RankCard(
              rank = i + 1, team = team, meta = meta, points = pm.first, movement = pm.second,
              expanded = open == i, distribution = SampleDist[i],
              stats = listOf("On 30/30 ballots", "Avg rank 1.43", "High 1st · Low 4th", "LW #1"),
              onToggle = { open = if (open == i) -1 else i },
            )
          }
        }
      }
      Column("340px") {
        Card(variant = CardVariant.Ticket, padding = "18px", stitchInset = "6px") {
          Div(attrs = Modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "12px").toAttrs()) {
            Eyebrow("Week 9 ballot", color = SwColor.field700, size = "10px")
            Countdown(remaining.toLong())
            Button("Cast Your Ballot", fullWidth = true)
          }
        }
        Div(attrs = Modifier.css("height" to "12px").toAttrs())
        Countdown(remaining.toLong(), tone = CountdownTone.OnField)
        Div(attrs = Modifier.css("height" to "12px").toAttrs())
        MatchupCard(
          tag = "Game of the week", note = "SAT",
          home = MatchupSide("Daisy St. Fightin' Beige", "5-3", PollStanding.Ranked(6), "108.9"),
          away = MatchupSide("Harbaugh/Stalions 4 Prison", "8-0", PollStanding.Ranked(1), "121.4"),
        )
        Div(attrs = Modifier.css("height" to "10px").toAttrs())
        MatchupCard(
          tag = "Ranked vs RV", note = "SAT",
          home = MatchupSide("This Is The Year", "6-2", PollStanding.ReceivingVotes, "103.5"),
          away = MatchupSide("Swamp Kings", "7-1", PollStanding.Ranked(3), "117.0"),
        )
        Div(attrs = Modifier.css("height" to "10px").toAttrs())
        MatchupCard(
          tag = "Week 6", note = "Win",
          home = MatchupSide("Swamp Kings", "4-2", PollStanding.Unranked, "98.2"),
          away = MatchupSide("Harbaugh/Stalions 4 Prison", "6-0", PollStanding.Unranked, "121.4", winner = true),
        )
        Div(attrs = Modifier.css("height" to "10px").toAttrs())
        MatchupCard(
          tag = "", note = NOT_FINAL,
          home = MatchupSide("Fink", "2-5", PollStanding.Unranked, "100.2"),
          away = MatchupSide("Daisy St. Fightin' Beige", "4-3", PollStanding.Unranked, "115.7", winner = true),
        )
      }
    }
  }
}

@Composable
private fun BallotComponents() {
  val ballot = remember { mutableStateListOf<String?>("Harbaugh/Stalions 4 Prison", "Swamp Kings", "MACtion Daddy", null) }
  var dragFrom by remember { mutableStateOf<Int?>(null) }
  var over by remember { mutableStateOf<Int?>(null) }
  Block("Ballot", "Slots · the field · banner · ballot review") {
    Banner(
      "Your ballot is in.",
      body = "Submitted Tue 9:14 PM. Change your mind? You've got until ${deadlineTime(nextFridayDeadline(rememberNow().toDouble()).toLong())}.",
      modifier = Modifier.css("margin-bottom" to "18px"),
    ) {
      Button("Amend", style = ButtonStyle.Caps)
      Button("Withdraw", variant = ButtonVariant.Outline, style = ButtonStyle.Caps)
    }
    Columns {
      Column("520px") {
        Div(attrs = Modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "7px").toAttrs()) {
          ballot.forEachIndexed { i, team ->
            BallotSlot(
              position = i + 1, team = team, meta = team?.let { "7-1 · LW #${i + 1}" },
              dropTarget = over == i && dragFrom != null, dragging = dragFrom == i,
              canMoveUp = i > 0, canMoveDown = i < ballot.lastIndex,
              onMoveUp = { val t = ballot[i - 1]; ballot[i - 1] = ballot[i]; ballot[i] = t },
              onMoveDown = { val t = ballot[i + 1]; ballot[i + 1] = ballot[i]; ballot[i] = t },
              onRemove = { ballot[i] = null },
              drag = SlotDragHandlers(
                onDragStart = { dragFrom = i },
                onDragOver = { over = it },
                onDrop = { target -> dragFrom?.let { from -> ballot.add(target, ballot.removeAt(from)) }; dragFrom = null; over = null },
                onDragEnd = { dragFrom = null; over = null },
              ),
            )
          }
        }
      }
      Column("320px") {
        Card(variant = CardVariant.Field) {
          PanelLabel("The field · 26 unranked", note = "BY RECORD")
          listOf(Triple("Fire Norvell", "6-2", "#4"), Triple("Wehrlebirds", "6-2", "#7"), Triple("Fink", "1-7", "—")).forEach { (t, r, lw) ->
            FieldTeamRow(t, r, lw, Modifier.css("margin-bottom" to "3px"), onAdd = {
              val k = ballot.indexOf(null); if (k >= 0) ballot[k] = t
            })
          }
        }
        BallotReviewCard(
          "A sound top three, but Fire Norvell is missing from a ballot the computers all have it on.",
          listOf(
            ReviewNoteView("Results", "You have MACtion Daddy 3rd, above Fire Norvell, who beat them 141.2–118.6 in week 6."),
            ReviewNoteView("Since last week", "Swamp Kings moved up from 4th to 2nd after a 30-point loss."),
          ),
          Modifier.css("margin-top" to "16px"),
          stale = true,
        )
        Card(Modifier.css("margin-top" to "16px"), variant = CardVariant.Field) {
          PanelLabel("Your week 7 ballot", note = "HOW THEY DID")
          PreviousPickRow(1, "BeLe", "Lost to From Worst to Thirst", "133.3–142.5", "L", won = false)
          PreviousPickRow(2, "Haser U", "Beat Wehrlebirds", "85.7–60.5 · $NOT_FINAL", "W", won = true)
          PreviousPickRow(3, "MACtion Daddy", "Scores not in yet", null, null, won = false)
        }
      }
    }
  }
}

@Composable
private fun CommishComponents() {
  Block("Commissioner", "Stats · submissions · wire · archive") {
    Div(attrs = Modifier.css("display" to "grid", "grid-template-columns" to "repeat(auto-fit,minmax(160px,1fr))", "gap" to "10px", "margin-bottom" to "22px").toAttrs()) {
      StatTile("26/30", "Ballots in"); StatTile("4", "Missing"); StatTile("2d 14h", "Until cutoff")
    }
    Columns(gap = "24px") {
      Column("580px") {
        SubmissionTable(footer = "+ 18 more on-time ballots · ballot contents visible to commish only") {
          SubmissionRow("Swamp Kings", "Harbaugh/Stalions 4 Prison", "Tue 9:14 PM", SubmissionStatus.Amended)
          SubmissionRow("MACtion Daddy", "MACtion Daddy", "Tue 8:02 PM", SubmissionStatus.OnTime)
          SubmissionRow("Team Mann U", "—", "Tue 6:11 PM", SubmissionStatus.Withdrawn)
        }
      }
      Column("320px") {
        Card(variant = CardVariant.Field) {
          PanelLabel("Wire · every submission", note = "EMAIL + PUSH ON")
          FeedRow("Swamp Kings amended their ballot", meta = "2M")
          FeedRow("MACtion Daddy submitted", meta = "1H")
        }
        Div(attrs = Modifier.css("height" to "16px").toAttrs())
        Div(attrs = Modifier.css("background" to SwColor.field900, "border-radius" to "28px", "padding" to "28px 14px",
          "display" to "flex", "flex-direction" to "column", "gap" to "10px").toAttrs()) {
          PushPreview("The Week 8 Poll is out", "Prison Boys hold at No. 1 (21 first-place votes). MACtion Daddy up to No. 2.", "now")
          val deadline = nextFridayDeadline(rememberNow().toDouble()).toLong()
          val leadMinutes = PollRules.REMINDER_LEAD_MINUTES[1]
          PushPreview(
            "${leadMinutes / 60} hours to the whistle",
            "You haven't voted in the Week 9 poll. Ballots close ${easternShortDayTime(deadline)} ET.",
            easternShortDayTime(deadline - leadMinutes * 60_000L),
          )
        }
      }
    }
    Div(attrs = Modifier.css("display" to "grid", "grid-template-columns" to "repeat(auto-fit,minmax(240px,1fr))", "gap" to "12px", "margin" to "28px 0").toAttrs()) {
      WeekCard(8, "Oct 23", "Harbaugh/Stalions 4 Prison", "21 first-place votes · 227 pts")
      WeekCard(7, "Oct 16", "Harbaugh/Stalions 4 Prison", "19 first-place votes · 222 pts")
    }
    RankHistoryTable(
      listOf("Wk 6", "Wk 7", "Wk 8"),
      listOf(
        RankHistoryRow("MACtion Daddy", listOf(PollStanding.Ranked(4), PollStanding.Ranked(3), PollStanding.Ranked(2))),
        RankHistoryRow("Wehrlebirds", listOf(PollStanding.Unranked, PollStanding.ReceivingVotes, PollStanding.Ranked(7))),
      ),
    )
    SortableTable(
      listOf(
        TableColumn<Pair<String, Int>>("Team", compareBy { it.first }, widthPx = 170, alignEnd = false, sticky = true) { Text(it.first) },
        TableColumn("Elo", compareByDescending { it.second }) { Text(it.second.toString()) },
      ),
      listOf("MACtion Daddy" to 1560, "BeLe" to 1586, "Wehrlebirds" to 1468),
      Modifier.css("margin-top" to "28px"),
      initialSort = 1,
    )
  }
}

/** Next Friday 11:00 PM ET for the samples: Saturday 03:00 UTC under EDT, 04:00 under EST. */
internal fun nextFridayDeadline(nowMs: Double): Double {
  val d = Date(nowMs)
  val target = Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 3, 0, 0, 0))
  val daysAhead = (6 - target.getUTCDay() + 7) % 7
  var t = target.getTime() + daysAhead * 86_400_000.0
  if (t <= nowMs) t += 7 * 86_400_000.0
  if (easternHour(t.toLong()) != PollRules.DEADLINE_HOUR) t += 3_600_000.0
  return t
}

@Composable
private fun LoadingComponents() {
  var shown by remember { mutableStateOf(0) }
  Block("Loading", "Skeleton · reveal") {
    Columns {
      Column("420px") {
        Skeleton {
          PageTitleSkeleton()
          SkeletonBlock("62px", Modifier.css("margin-top" to "18px"))
        }
      }
      Column("420px") {
        Button("Replay reveal", variant = ButtonVariant.Outline, style = ButtonStyle.CapsSm, onDark = true,
          onClick = { shown++ })
        key(shown) {
          Reveal(Modifier.css("margin-top" to "12px")) {
            RankCard(rank = 1, team = "Harbaugh/Stalions 4 Prison", meta = "8-0 · 21 first-place votes", points = 227,
              movement = Movement.Same)
          }
        }
      }
    }
  }
}
